// components/ImagesListModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaEye, FaTrash } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';
import { MoldService } from '../../../../service/api/mold-service';
import '../../viewallimagesmodal/view-all-images-modal.css';
import EditTextModal from '../edittextmodal/edit-text-modal';
import { ZoomableImageModal } from 'Core/components';
import { downloadImage } from 'Core/utils';
import { useDocumentUpload } from 'Core/hooks';
import { MOLD_TYPE } from '../../../constants/moldConstants';
import { sanitizeUploadKey } from '../../sanitizeUploadKeys/sanitizeUploadKeys';

const ImagesListModal = ({ 
  show, 
  onClose, 
  segmentName, 
  images = [], 
  onDelete,
  moldInfoID,
  buttonLabel,
  maintenanceId = 0,
  context = 'maintenance',
  savedMaintenanceActivityData,
  onImageSaved
}) => {
  const dispatch = useDispatch();
  const { imageDocuments } = useDocumentUpload();
  const [selectedImg, setSelectedImg] = useState(null);
  const [fieldType, setFieldType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingEditedImage, setIsUploadingEditedImage] = useState(false);
  
  // State for zoomable image modal
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editedImageBlob, setEditedImageBlob] = useState(null);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);

  useEffect(() => {
    if (!show) {
      setDeleteConfirmIndex(null);
    }
  }, [show]);

  const handleOpenEdit = async (img, type) => {
    // Check if image has a valid ID
    if (!img.id || img.id === null || img.id === undefined) {
      dispatch(showToast({ 
        message: 'Cannot edit image metadata: Image ID is missing. Please refresh and try again.', 
        type: 'danger' 
      }));
      return;
    }
    
    setSelectedImg(img);
    setFieldType(type);
    
    // Fetch current title/description from API
    try {
      let response;
      if (type === 'Title') {
        response = await MoldService.getImageSegmentTitleById(img.id);
      } else if (type === 'Description') {
        response = await MoldService.getImageSegmentDescriptionById(img.id);
      }
      
      // Update the image object with the fetched data
      if (response && response.status === 200 && response.response) {
        const fieldName = type.toLowerCase();
        // Handle the case where response.response is an array
        let fetchedValue;
        if (Array.isArray(response.response)) {
          // If it's an array, get the first item's value
          fetchedValue = response.response[0]?.[fieldName] || response.response[0]?.Title || response.response[0]?.Description;
        } else {
          // If it's an object, use the original logic
          fetchedValue = response.response[fieldName] || response.response.Title || response.response.Description;
        }
        
        console.log('ImagesListModal: Fetched value for', type, ':', fetchedValue);
        console.log('ImagesListModal: API response structure:', response.response);
        
        // Only update if the fetched value is not null or empty
        if (fetchedValue && fetchedValue.trim() !== '') {
          img[fieldName] = fetchedValue;
          // Force re-render by updating the selectedImg state
          setSelectedImg({ ...img });
          console.log('ImagesListModal: Updated image with', fieldName, ':', fetchedValue);
        } else {
          console.log('ImagesListModal: No valid value found for', fieldName);
        }
      }
    } catch (error) {
      console.error(`Error fetching ${type.toLowerCase()}:`, error);
      // Continue with the existing value if API call fails
    }
  };

  const handleSaveEdit = async (newValue) => {
    if (!selectedImg) return;
    
    // Double-check if image has a valid ID before making API call
    if (!selectedImg.id || selectedImg.id === null || selectedImg.id === undefined) {
      dispatch(showToast({ 
        message: 'Cannot save image metadata: Image ID is missing. Please refresh and try again.', 
        type: 'danger' 
      }));
      return;
    }
    
    setIsSaving(true);
    try {
      let response;
      
      // Call the appropriate API based on field type
      if (fieldType === 'Title') {
        response = await MoldService.updateImageSegmentTitle(selectedImg.id, newValue);
      } else if (fieldType === 'Description') {
        response = await MoldService.updateImageSegmentDescription(selectedImg.id, newValue);
      }
      
      // Check if the API call was successful (status 200)
      if (response && response.status === 200) {
        // Update the local state for better UX
        selectedImg[fieldType.toLowerCase()] = newValue;
        
        // Show success message and close popup
        if (fieldType === 'Title') {
          dispatch(showToast({ message: 'Title Saved Successfully', type: 'success' }));
        } else {
          dispatch(showToast({ message: 'Description Saved Successfully', type: 'success' }));
        }
        
        // Close the edit modal
        setSelectedImg(null);
        setFieldType(null);
      } else {
        throw new Error('API call failed');
      }
    } catch (error) {
      console.error('Error updating image metadata:', error);
      dispatch(showToast({ message: `Failed to update ${fieldType.toLowerCase()}`, type: 'danger' }));
    } finally {
      setIsSaving(false);
    }
  };

  // Handler for opening zoom modal
  const handleImageClick = (e, image) => {
    e.stopPropagation();
    setSelectedImage(image);
    setShowZoomModal(true);
  };

  // Handler for closing zoom modal
  const handleCloseZoomModal = () => {
    setShowZoomModal(false);
    setSelectedImage(null);
  };

  // Handler for download from zoom modal
  const handleZoomModalDownload = (imageSrc) => {
    downloadImage(imageSrc);
  };

  const handleConfirmDeleteImage = async () => {
    if (deleteConfirmIndex === null || typeof onDelete !== 'function') {
      setDeleteConfirmIndex(null);
      return;
    }
    const indexToDelete = deleteConfirmIndex;
    setDeleteConfirmIndex(null);
    try {
      await onDelete(indexToDelete);
    } catch (error) {
      console.error('ImagesListModal: delete failed:', error);
    }
  };

  // Handler for saving edited image from ZoomableImageModal
  const handleSaveEditedImage = async (blob) => {
    console.log('🖼️ handleSaveEditedImage called with blob:', blob);
    console.log('🖼️ selectedImage:', selectedImage);
    
    if (!blob) {
      console.error('❌ No blob provided');
      dispatch(showToast({ 
        message: 'No image data to save. Please try editing the image again.', 
        type: 'danger' 
      }));
      return;
    }

    if (!selectedImage) {
      console.error('❌ No selected image');
      dispatch(showToast({ 
        message: 'No image selected. Please try again.', 
        type: 'danger' 
      }));
      return;
    }

    // Validate blob
    if (!(blob instanceof Blob)) {
      console.error('❌ Invalid blob type:', typeof blob);
      dispatch(showToast({ 
        message: 'Invalid image data. Please try editing the image again.', 
        type: 'danger' 
      }));
      return;
    }

    if (blob.size === 0) {
      console.error('❌ Blob is empty');
      dispatch(showToast({ 
        message: 'Image data is empty. Please try editing the image again.', 
        type: 'danger' 
      }));
      return;
    }

    setIsUploadingEditedImage(true);
    
    try {
      // Convert blob to File
      const safeTimestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const originalFileName = selectedImage.name || selectedImage.fileName || `edited_image_${safeTimestamp}.jpg`;
      // Ensure file extension is .jpg
      const fileName = originalFileName.endsWith('.jpg') || originalFileName.endsWith('.jpeg') 
        ? originalFileName 
        : `${originalFileName.replace(/\.[^/.]+$/, '')}.jpg`;
      
      console.log('📁 Creating file from blob:', { fileName, blobSize: blob.size, blobType: blob.type });
      
      let file;
      try {
        file = new File([blob], fileName, { type: 'image/jpeg' });
        console.log('✅ File created successfully:', { name: file.name, size: file.size, type: file.type });
      } catch (fileError) {
        console.error('❌ Error creating File from blob:', fileError);
        // Fallback: try with Blob if File constructor fails
        file = blob;
        console.log('⚠️ Using blob directly as fallback');
      }

      // Determine upload category based on context
      let uploadCategory;
      if (context === 'receiving') {
        uploadCategory = sanitizeUploadKey(MOLD_TYPE.MOLD_RECEIVING);
      } else if (context === 'release') {
        uploadCategory = sanitizeUploadKey(MOLD_TYPE.MOLD_RELEASE);
      } else {
        uploadCategory = sanitizeUploadKey(MOLD_TYPE.MOLD_MAINTENANCE);
      }

      const fileCategoryId = sanitizeUploadKey(MOLD_TYPE.WEBCAM_CAPTURED_IMAGE);

      console.log('📤 Uploading file with category:', uploadCategory, 'fileCategoryId:', fileCategoryId);

      // Create FormData for upload
      const docFormData = new FormData();
      docFormData.append('file', file);
      docFormData.append('fileCategoryId', fileCategoryId);
      docFormData.append('uploadCategory', uploadCategory);

      // Upload the edited image
      console.log('📤 Calling imageDocuments...');
      const uploadResult = await imageDocuments(docFormData);
      console.log('📤 Upload result:', uploadResult);

      if (!uploadResult) {
        throw new Error('Upload returned null. The file may be too large or the upload service is unavailable.');
      }

      if (!uploadResult.relativePath) {
        console.error('❌ Upload result missing relativePath:', uploadResult);
        throw new Error('Upload succeeded but no file path was returned. Please try again.');
      }

      console.log('✅ Upload successful, relativePath:', uploadResult.relativePath);
        // Prepare segment data for saveOrUpdateMoldImageSegments
        let segmentData = {
          moldInfoID: moldInfoID || 0,
          imageUrl: uploadResult.relativePath,
          segment: segmentName,
          record: buttonLabel || '',
          maintenanceId: maintenanceId || 0,
          title: selectedImage.title || '',
          description: selectedImage.description || '',
        };

        // Determine category based on context
        if (context === 'receiving') {
          segmentData.category = 'Mold-Receiving';
        } else if (context === 'release') {
          segmentData.category = 'Mold-Release';
        } else if (context === 'maintenance') {
          segmentData.category = 'Mold-Maintenance';
        } else {
          segmentData.category = 'Mold-Maintenance';
        }

        // Add actionImageId and subActionImageId if available
        if (savedMaintenanceActivityData) {
          segmentData.actionImageId = savedMaintenanceActivityData.actionImageId || 0;
          segmentData.subActionImageId = savedMaintenanceActivityData.subActionImageId || 0;
        } else {
          segmentData.actionImageId = 0;
          segmentData.subActionImageId = 0;
        }

        // If editing existing image (has ID), include the ID for update
        if (selectedImage.id) {
          segmentData.id = selectedImage.id;
        }

        // Call saveOrUpdateMoldImageSegments API
        console.log('💾 Calling saveUpdateImageSegments with data:', segmentData);
        const segmentResponse = await MoldService.saveUpdateImageSegments(segmentData);
        console.log('💾 Segment response:', segmentResponse);

        if (!segmentResponse) {
          throw new Error('No response from save image segment API');
        }

        if (segmentResponse.status === 200) {
          console.log('✅ Image segment saved successfully');
          dispatch(showToast({ 
            message: 'Edited image saved successfully!', 
            type: 'success' 
          }));

          // Close the zoom modal
          setShowZoomModal(false);
          setSelectedImage(null);

          // Call refresh callback if provided to reload images from API
          if (typeof onImageSaved === 'function') {
            console.log('🔄 Calling onImageSaved callback to refresh images...');
            onImageSaved();
          }
        } else {
          const errorMsg = segmentResponse.errorMessage?.message || 
                          segmentResponse.message || 
                          `API returned status ${segmentResponse.status}`;
          console.error('❌ API error:', errorMsg);
          throw new Error(`Failed to save image segment: ${errorMsg}`);
        }
    } catch (error) {
      console.error('❌ Error saving edited image:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      let errorMessage = 'Failed to save edited image';
      if (error.message) {
        errorMessage += ': ' + error.message;
      } else if (error.response) {
        errorMessage += ': ' + (error.response.data?.message || 'Server error');
      } else if (error.request) {
        errorMessage += ': Network error. Please check your connection.';
      }
      
      dispatch(showToast({ 
        message: errorMessage, 
        type: 'danger' 
      }));
    } finally {
      setIsUploadingEditedImage(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Manage Images - {segmentName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {images.length === 0 ? (
            <p>No images uploaded.</p>
          ) : (
            <ul className="list-group">
              {images.map((img, index) => (
                <li
                  key={index}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                     {/* Thumbnail + name */}
                  <div className="d-flex align-items-center gap-2">
                    <img
                      src={img.path}
                      alt={img.title || img.name}
                      style={{
                        width: '40px',
                        height: '40px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => handleImageClick(e, img)}
                    />
                    <div className="d-flex flex-column">
                      <span className="fw-bold">{img.title || img.name}</span>
                      {img.description && (
                        <small className="text-muted">{img.description}</small>
                      )}
                    </div>
                    </div>

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                      onClick={() => window.open(img.path || img.filePath, '_blank')}
                      title="View Image"
                    >
                      <FaEye /> View
                    </button>
                    <button
                      className="btn btn-outline-warning btn-sm"
                      onClick={() => handleOpenEdit(img, 'Title')}
                    >
                      Title
                    </button>
                    <button
                      className="btn btn-outline-warning btn-sm"
                      onClick={() => handleOpenEdit(img, 'Description')}
                    >
                      Description
                    </button>
                     <button
                      type="button"
                      className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                      onClick={() => {
                        if (typeof onDelete === 'function') {
                          setDeleteConfirmIndex(index);
                        }
                      }}
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-md-imp btn-primary" onClick={onClose}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {selectedImg && fieldType && (
        <EditTextModal
          show={true}
          onClose={() => {
            setSelectedImg(null);
            setFieldType(null);
          }}
          onSave={handleSaveEdit}
          fieldType={fieldType}
          initialValue={selectedImg[fieldType.toLowerCase()]}
          isSaving={isSaving}
        />
      )}

      {/* Zoomable Image Modal */}
      <ZoomableImageModal
        show={showZoomModal}
        onHide={handleCloseZoomModal}
        imageSrc={selectedImage?.path}
        imageAlt={selectedImage?.title || selectedImage?.name || 'Manage Images'}
        onDownload={handleZoomModalDownload}
        enableEditor={true}
        onSaveEdited={handleSaveEditedImage}
      />

      <Modal
        show={deleteConfirmIndex !== null}
        onHide={() => setDeleteConfirmIndex(null)}
        centered
        size="sm"
        backdrop="static"
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Do you really want to delete this image? It may be removed permanently and cannot be recovered.
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end gap-2">
          <Button variant="secondary" onClick={() => setDeleteConfirmIndex(null)}>
            No
          </Button>
          <Button variant="danger" onClick={handleConfirmDeleteImage}>
            Yes, delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ImagesListModal;
